import React, { useEffect, useRef, useState } from 'react';
import './FlagDialog.css';

/** Metro-style dialog for the "Flag for Marius" feature: flat tile, big
 *  type, square buttons. Replaces window.prompt / window.confirm.
 *  variant="note" reuses it for the PERSISTENT billing note (standing
 *  instructions that stay on the row month after month). */
export type FlagDialogMode = 'flag' | 'edit' | 'clear';
export type FlagDialogVariant = 'flag' | 'note';

export interface FlagDialogProps {
  mode: FlagDialogMode;
  variant?: FlagDialogVariant;
  customerName: string;
  note?: string | null;
  flaggedBy?: string | null;
  flaggedAt?: string | null;
  busy?: boolean;
  onConfirm: (note: string) => void;
  onClear?: () => void;          // "edit" mode also offers clearing
  onCancel: () => void;
}

const TITLES: Record<FlagDialogVariant, Record<FlagDialogMode, string>> = {
  flag: { flag: 'Flag for Marius', edit: 'Flag note', clear: 'Clear the flag?' },
  note: { flag: 'Billing note', edit: 'Billing note', clear: 'Remove the billing note?' },
};

export default function FlagDialog({ mode, variant = 'flag', customerName, note, flaggedBy, flaggedAt, busy, onConfirm, onClear, onCancel }: FlagDialogProps) {
  const isNote = variant === 'note';
  const [text, setText] = useState(note || '');
  const box = useRef<HTMLTextAreaElement>(null);
  useEffect(() => { if (mode !== 'clear') box.current?.focus(); }, [mode]);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey || mode === 'clear')) { e.preventDefault(); mode === 'clear' ? onClear?.() : onConfirm(text); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mode, text, onCancel, onConfirm, onClear]);

  const when = flaggedAt ? new Date(flaggedAt).toLocaleString('en-ZA', { dateStyle: 'medium', timeStyle: 'short' }) : null;

  return (
    <div className="mflag-overlay" onMouseDown={onCancel}>
      <div className={`mflag ${mode} ${variant}`} role="dialog" aria-modal="true" aria-label={TITLES[variant][mode]} onMouseDown={e => e.stopPropagation()}>
        <div className="mflag-head">
          <span className="mflag-glyph">{isNote ? '📝' : '⚑'}</span>
          <div>
            <div className="mflag-title">{TITLES[variant][mode]}</div>
            <div className="mflag-customer">{customerName}</div>
          </div>
        </div>
        <div className="mflag-body">
          {mode === 'clear' ? (
            <>
              <p className="mflag-text">{isNote ? 'The note is removed from the row for everyone.' : 'The flag comes off and the row goes back to normal for everyone.'}</p>
              {note && <blockquote className="mflag-quote">{note}</blockquote>}
              {(flaggedBy || when) && <p className="mflag-meta">{isNote ? 'Last edited' : 'Flagged'}{flaggedBy ? ` by ${flaggedBy}` : ''}{when ? ` · ${when}` : ''}</p>}
            </>
          ) : (
            <>
              <label className="mflag-label" htmlFor="mflag-note">
                {isNote ? 'Standing instructions for this customer' : mode === 'flag' ? 'What should Marius look at?' : 'Note'} <span>{isNote ? 'stays month to month' : 'optional'}</span>
              </label>
              <textarea id="mflag-note" ref={box} className="mflag-input" rows={isNote ? 5 : 3} value={text} maxLength={isNote ? 2000 : 500}
                        placeholder={isNote
                          ? 'e.g. Bill the Teams add-on separately · PO number required on every invoice · fibre is invoiced by WebCafe'
                          : 'e.g. Seat count looks wrong vs CloudBlue, please check before the run'}
                        onChange={e => setText(e.target.value)} disabled={busy} />
              <div className="mflag-hint">
                {isNote
                  ? ((flaggedBy || when) ? `Last edited${flaggedBy ? ` by ${flaggedBy}` : ''}${when ? ` · ${when}` : ''}` : 'Everyone sees it on the Invoice Prep row, every month, until it is removed.')
                  : mode === 'flag' ? 'Everyone sees the flag on Invoice Prep until it is cleared.' : (flaggedBy || when) ? `Flagged${flaggedBy ? ` by ${flaggedBy}` : ''}${when ? ` · ${when}` : ''}` : ''}
                <span className="mflag-kbd">Ctrl+Enter</span>
              </div>
            </>
          )}
        </div>
        <div className="mflag-actions">
          {mode === 'clear' ? (
            <>
              <button className="mflag-btn primary" onClick={onClear} disabled={busy} autoFocus>{isNote ? 'Remove note' : 'Clear flag'}</button>
              <button className="mflag-btn" onClick={onCancel} disabled={busy}>Keep it</button>
            </>
          ) : (
            <>
              <button className="mflag-btn primary" onClick={() => onConfirm(text)} disabled={busy}>
                {busy ? 'Saving…' : isNote ? 'Save note' : mode === 'flag' ? 'Flag customer' : 'Save note'}
              </button>
              {mode === 'edit' && onClear && <button className="mflag-btn danger" onClick={onClear} disabled={busy}>{isNote ? 'Remove note' : 'Clear flag'}</button>}
              <button className="mflag-btn" onClick={onCancel} disabled={busy}>Cancel</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
